/**
 * Resolves a clientFilter value (UUID, name, numeric string, or 'all')
 * to the needed identifiers for different tables.
 */
import { supabase } from '@/integrations/supabase/client'

interface ResolvedClient {
  numericId: number | null       // For obsync_dynamic_reports.client_id
  workspaceName: string | null   // For masterinbox_replies.workspace_name
}

let cache: Record<string, ResolvedClient> = {}

export async function resolveClient(clientFilter: string | undefined): Promise<ResolvedClient> {
  if (!clientFilter || clientFilter === 'all') return { numericId: null, workspaceName: null }

  if (cache[clientFilter]) return cache[clientFilter]

  // Try UUID lookup
  const { data: byId } = await supabase
    .from('clients')
    .select('client_id, name')
    .eq('id', clientFilter)
    .maybeSingle()

  if (byId) {
    const result = { numericId: Number(byId.client_id), workspaceName: byId.name }
    cache[clientFilter] = result
    return result
  }

  // Try name lookup
  const { data: byName } = await supabase
    .from('clients')
    .select('client_id, name')
    .eq('name', clientFilter)
    .maybeSingle()

  if (byName) {
    const result = { numericId: Number(byName.client_id), workspaceName: byName.name }
    cache[clientFilter] = result
    return result
  }

  // Try numeric lookup
  if (/^\d+$/.test(clientFilter)) {
    const { data: byNum } = await supabase
      .from('clients')
      .select('client_id, name')
      .eq('client_id', parseInt(clientFilter, 10))
      .maybeSingle()

    if (byNum) {
      const result = { numericId: Number(byNum.client_id), workspaceName: byNum.name }
      cache[clientFilter] = result
      return result
    }
  }

  return { numericId: null, workspaceName: null }
}

/** Backwards-compatible: resolve to numeric client_id only */
export async function resolveClientFilter(clientFilter: string | undefined): Promise<number | null> {
  const { numericId } = await resolveClient(clientFilter)
  return numericId
}
